import { PersonIcon } from '@radix-ui/react-icons'
import ReactMarkdown, { type Components } from 'react-markdown'
import { Link } from 'react-router'
import { parseSafeZone, safeZoneImageStyle } from '#app/domains/program/pure'
import { type EventWithSeatsAndTickets } from '#app/utils/providers/events.server.js'

const H1Renderer: Components['h1'] = ({ children }) => (
	<h2 className="font-display mt-8 mb-3 text-2xl font-semibold">{children}</h2>
)

const H2Renderer: Components['h2'] = ({ children }) => (
	<h2 className="font-display mt-8 mb-3 text-2xl font-semibold">{children}</h2>
)

const H3Renderer: Components['h3'] = ({ children }) => (
	<h3 className="font-display mt-6 mb-2 text-xl font-semibold">{children}</h3>
)

const H4Renderer: Components['h4'] = ({ children }) => (
	<h4 className="mt-5 mb-2 text-lg font-bold">{children}</h4>
)

const H5Renderer: Components['h5'] = ({ children }) => (
	<h5 className="mt-4 mb-2 text-base font-bold">{children}</h5>
)

const H6Renderer: Components['h6'] = ({ children }) => (
	<h6 className="mt-4 mb-2 text-sm font-bold">{children}</h6>
)

const ParagraphRenderer: Components['p'] = ({ children }) => (
	<p className="mb-4 text-[1.0625rem] leading-relaxed text-pretty">
		{children}
	</p>
)

const LinkRenderer: Components['a'] = ({ href, children }) => (
	<a
		href={href}
		className="text-ember-ink decoration-ember/40 hover:decoration-ember font-semibold underline underline-offset-3"
		target="_blank"
		rel="noopener noreferrer"
	>
		{children}
	</a>
)

const UnorderedListRenderer: Components['ul'] = ({ children }) => (
	<ul className="[&>li]:before:bg-ember mb-5 ml-1 list-none space-y-1.5 [&>li]:relative [&>li]:pl-5 [&>li]:before:absolute [&>li]:before:top-[0.6em] [&>li]:before:left-0 [&>li]:before:h-1.5 [&>li]:before:w-1.5 [&>li]:before:rounded-full [&>li]:before:content-['']">
		{children}
	</ul>
)

const OrderedListRenderer: Components['ol'] = ({ children }) => (
	<ol className="marker:text-ember-ink mb-5 ml-5 list-decimal space-y-1.5 marker:font-bold">
		{children}
	</ol>
)

const ListItemRenderer: Components['li'] = ({ children }) => (
	<li className="text-[1.0625rem] leading-relaxed">{children}</li>
)

const BlockquoteRenderer: Components['blockquote'] = ({ children }) => (
	<blockquote className="border-lamp font-display mb-5 border-l-4 pl-4 text-lg italic">
		{children}
	</blockquote>
)

const CodeRenderer: Components['code'] = ({ className, children }) => {
	const match = /language-(\w+)/.exec(className || '')
	return match ? (
		<pre className="bg-base-200 mb-4 overflow-x-auto rounded-2xl p-4">
			<code className={`language-${match[1]} text-sm`}>{children}</code>
		</pre>
	) : (
		<code className="bg-base-200 rounded px-1 py-0.5 text-sm">{children}</code>
	)
}

const TableRenderer: Components['table'] = ({ children }) => (
	<div className="mb-4 overflow-x-auto">
		<table className="table w-full">{children}</table>
	</div>
)

const TableHeadRenderer: Components['thead'] = ({ children }) => (
	<thead>{children}</thead>
)

const TableBodyRenderer: Components['tbody'] = ({ children }) => (
	<tbody>{children}</tbody>
)

const TableRowRenderer: Components['tr'] = ({ children }) => <tr>{children}</tr>

const TableCellRenderer: Components['td'] = ({ children }) => (
	<td>{children}</td>
)

const TableHeaderCellRenderer: Components['th'] = ({ children }) => (
	<th>{children}</th>
)

const RuleRenderer: Components['hr'] = () => (
	<hr className="border-line my-8 border-0 border-t-2 border-dashed" />
)

// photos in the text sit like pinned prints beside it
const ImageRenderer: Components['img'] = ({ src, alt }) => (
	<img
		src={src}
		alt={alt ?? ''}
		className="float-right mb-3 ml-4 h-auto w-28 rotate-2 rounded-xl border-4 border-white object-cover shadow-[0_10px_24px_-12px_rgb(40_25_10/0.6)] sm:w-36"
		loading="lazy"
		decoding="async"
	/>
)

export const buildMarkdownComponents = (): Components => ({
	h1: H1Renderer,
	h2: H2Renderer,
	h3: H3Renderer,
	h4: H4Renderer,
	h5: H5Renderer,
	h6: H6Renderer,
	p: ParagraphRenderer,
	a: LinkRenderer,
	ul: UnorderedListRenderer,
	ol: OrderedListRenderer,
	li: ListItemRenderer,
	blockquote: BlockquoteRenderer,
	code: CodeRenderer,
	table: TableRenderer,
	thead: TableHeadRenderer,
	tbody: TableBodyRenderer,
	tr: TableRowRenderer,
	td: TableCellRenderer,
	th: TableHeaderCellRenderer,
	hr: RuleRenderer,
	img: ImageRenderer,
})

/**
 * The concert's own text (markdown from the organisers) and the artists
 * with their own pages.
 */
export function EventDescription({
	event,
}: {
	event: Pick<EventWithSeatsAndTickets, 'description' | 'featuring'>
}) {
	return (
		<>
			{event.description ? (
				<div className="flow-root">
					<ReactMarkdown components={buildMarkdownComponents()}>
						{event.description}
					</ReactMarkdown>
				</div>
			) : (
				<p className="font-display text-muted-ink text-lg">
					Meir om konserten kjem snart.
				</p>
			)}
			{event.featuring?.length ? (
				<div className="mt-10">
					<h2 className="font-display mb-4 text-2xl font-semibold">Artistar</h2>
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						{event.featuring.map((performer) => (
							<Link
								key={performer.id}
								to={`/performers/${performer.slug}`}
								viewTransition
								className="group border-line bg-sheet ease-spring hover:border-ember/50 flex items-center gap-3 rounded-2xl border p-3 shadow-[0_12px_28px_-24px_rgb(60_40_20/0.6)] transition duration-500 hover:-translate-y-0.5"
							>
								{performer.imageUrl ? (
									<img
										src={performer.imageUrl}
										alt=""
										className="bg-base-200 h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white"
										style={safeZoneImageStyle(
											parseSafeZone(performer.imageSafeZone),
											1,
										)}
										loading="lazy"
									/>
								) : (
									<div className="bg-navy-2 text-lamp flex h-14 w-14 shrink-0 items-center justify-center rounded-full">
										<PersonIcon className="h-6 w-6" aria-hidden="true" />
									</div>
								)}
								<div className="min-w-0">
									<p
										className="font-display text-lg leading-tight font-semibold"
										// flies into the performer page's heading
										style={{
											viewTransitionName: `performer-${performer.slug}`,
										}}
									>
										{performer.name}
									</p>
									<p className="text-muted-ink line-clamp-2 text-sm">
										{performer.shortBio ?? 'Sjå artistsida'}
									</p>
								</div>
							</Link>
						))}
					</div>
				</div>
			) : null}
		</>
	)
}
